import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout,
  fromYear,
  toYear,
  ...props
}) {
  // For date picker with year/month dropdowns
  const useDropdowns = captionLayout === "dropdown-buttons" || captionLayout === "dropdown";
  const dropdownProps = useDropdowns ? {
    captionLayout: "dropdown-buttons",
    fromYear: fromYear || 2000,
    toYear: toYear || new Date().getFullYear(),
  } : {};

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 touch-manipulation", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: cn(
          "flex pt-1 relative items-center",
          useDropdowns ? "justify-between px-2" : "justify-center"
        ),
        caption_label: useDropdowns ? "hidden" : "text-sm font-medium",
        caption_dropdowns: "flex items-center gap-2",
        nav: cn(
          "flex items-center",
          useDropdowns ? "gap-1" : "space-x-1"
        ),
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-9 w-9 bg-transparent p-0 opacity-70 hover:opacity-100 border-white/10 touch-manipulation"
        ),
        nav_button_previous: useDropdowns ? "" : "absolute left-1",
        nav_button_next: useDropdowns ? "" : "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 touch-manipulation"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        dropdown: "px-3 py-2 bg-popover border border-white/10 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer touch-manipulation min-h-[44px]",
        dropdown_month: "",
        dropdown_year: "",
        vhidden: "sr-only",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...iconProps }) => (
          <ChevronLeft className={cn("h-5 w-5", className)} {...iconProps} />
        ),
        IconRight: ({ className, ...iconProps }) => (
          <ChevronRight className={cn("h-5 w-5", className)} {...iconProps} />
        ),
      }}
      {...dropdownProps}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
